<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-12">
        <h2 class="mb-4">📊 Care Log Reports</h2>
        
        <!-- Report Filters -->
        <div class="card mb-4">
          <div class="card-header">
            <h5 class="mb-0">Report Filters</h5>
          </div>
          <div class="card-body">
            <form @submit.prevent="generateReport">
              <div class="row">
                <div class="col-md-4">
                  <label for="startDate" class="form-label">Start Date</label>
                  <input 
                    type="date" 
                    id="startDate" 
                    v-model="filters.startDate" 
                    class="form-control"
                    required
                  >
                </div>
                <div class="col-md-4">
                  <label for="endDate" class="form-label">End Date</label>
                  <input 
                    type="date" 
                    id="endDate" 
                    v-model="filters.endDate" 
                    class="form-control"
                    required
                  >
                </div>
                <div class="col-md-4">
                  <label for="keywords" class="form-label">Keywords (comma-separated)</label>
                  <input 
                    type="text" 
                    id="keywords" 
                    v-model="filters.keywords" 
                    class="form-control"
                    placeholder="e.g., medication, pain, sleep"
                  >
                </div>
              </div>
              <div class="row mt-3">
                <div class="col-12">
                  <button type="submit" class="btn btn-primary" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    Generate Report
                  </button>
                  <button type="button" class="btn btn-secondary ms-2" @click="clearFilters">
                    Clear Filters
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>

        <!-- Report Results -->
        <div v-if="reportData.length > 0" class="card">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Report Results</h5>
            <span class="badge bg-primary">{{ reportData.length }} records found</span>
          </div>
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-striped table-hover mb-0">
                <thead class="table-dark">
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Author</th>
                    <th>Entry Type</th>
                    <th>Text</th>
                    <th>Tags</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="record in reportData" :key="record._id">
                    <td>{{ formatDate(record.createdAt) }}</td>
                    <td>{{ formatTime(record.createdAt) }}</td>
                    <td>{{ record.authorName }}</td>
                    <td>
                      <span class="badge" :class="getEntryTypeBadgeClass(record.entryType)">
                        {{ record.entryType }}
                      </span>
                    </td>
                    <td class="text-wrap" style="max-width: 300px;">
                      <span v-if="record.text" v-html="highlightKeywords(record.text)"></span>
                      <span v-else class="text-muted">No text</span>
                    </td>
                    <td>
                      <span v-if="record.tags && record.tags.length > 0">
                        <span 
                          v-for="tag in record.tags" 
                          :key="tag" 
                          class="badge bg-secondary me-1"
                        >
                          {{ tag }}
                        </span>
                      </span>
                      <span v-else class="text-muted">No tags</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- No Results Message -->
        <div v-else-if="reportGenerated && reportData.length === 0" class="alert alert-info">
          <h5>No records found</h5>
          <p>No care log entries match your current filters. Try adjusting your date range or keywords.</p>
        </div>

        <!-- Error Message -->
        <div v-if="error" class="alert alert-danger">
          <h5>Error</h5>
          <p>{{ error }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'Report',
  data() {
    return {
      filters: {
        startDate: '',
        endDate: '',
        keywords: ''
      },
      reportData: [],
      loading: false,
      error: null,
      reportGenerated: false
    };
  },
  mounted() {
    // Set default date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);
    
    this.filters.endDate = today.toISOString().split('T')[0];
    this.filters.startDate = thirtyDaysAgo.toISOString().split('T')[0];
  },
  methods: {
    async generateReport() {
      this.loading = true;
      this.error = null;
      this.reportGenerated = false;

      try {
        const params = new URLSearchParams();
        params.append('startDate', this.filters.startDate);
        params.append('endDate', this.filters.endDate);
        
        if (this.filters.keywords.trim()) {
          params.append('keywords', this.filters.keywords.trim());
        }

        const response = await axios.get(`/api/care-logs/reports?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('alnasir-jwt')}`
          }
        });

        this.reportData = response.data.data;
        this.reportGenerated = true;
      } catch (err) {
        this.error = err.response?.data?.error?.message || 'Failed to generate report';
        console.error('Report generation error:', err);
      } finally {
        this.loading = false;
      }
    },
    clearFilters() {
      this.filters = {
        startDate: '',
        endDate: '',
        keywords: ''
      };
      this.reportData = [];
      this.error = null;
      this.reportGenerated = false;
      
      // Reset to default date range
      const today = new Date();
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(today.getDate() - 30);
      
      this.filters.endDate = today.toISOString().split('T')[0];
      this.filters.startDate = thirtyDaysAgo.toISOString().split('T')[0];
    },
    formatDate(dateString) {
      return new Date(dateString).toISOString().split('T')[0];
    },
    formatTime(dateString) {
      return new Date(dateString).toLocaleTimeString();
    },
    getEntryTypeBadgeClass(entryType) {
      const classes = {
        'note': 'bg-primary',
        'bowel-movement': 'bg-warning',
        'urination': 'bg-info',
        'default': 'bg-secondary'
      };
      return classes[entryType] || classes.default;
    },
    highlightKeywords(text) {
      if (!this.filters.keywords.trim()) {
        return text;
      }

      const keywords = this.filters.keywords.split(',').map(k => k.trim()).filter(k => k.length > 0);
      let highlightedText = text;

      keywords.forEach(keyword => {
        const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        highlightedText = highlightedText.replace(regex, '<mark>$1</mark>');
      });

      return highlightedText;
    }
  }
};
</script>

<style scoped>
.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}

.text-wrap {
  word-wrap: break-word;
  word-break: break-word;
}

mark {
  background-color: #fff3cd;
  padding: 0.1em 0.2em;
  border-radius: 0.2em;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}
</style>
